import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BallotWarning } from "@/components/BallotWarning";
import { usePlatformCopy } from "@/hooks/usePlatformCopy";
import {
  Search, CheckCircle2, FileCheck2, Fingerprint, Scale, ShieldCheck, EyeOff, Lock, Link2, PenLine, UserCheck,
  MailCheck, Users, HandHeart, Landmark, ScrollText,
} from "lucide-react";

const DETAIL_STEPS = [
  { icon: FileCheck2, title: "1. Declare", text: "You make your own WitnessYourVote declaration for the candidate you voted for — voluntarily, after voting through the official process." },
  { icon: MailCheck, title: "2. Verify", text: "You confirm your email address. This protects the system and unlocks Mutual Witness status." },
  { icon: PenLine, title: "3. Sign", text: "You electronically sign your declaration and receive a unique declaration number." },
  { icon: Users, title: "4. Get Witnessed", text: "Choose someone you trust — or ask WitnessYourVote to find a participating witness near you." },
  { icon: UserCheck, title: "5. Witness", text: "Your witness confirms that you personally made and signed the declaration. They do not inspect your official ballot." },
  { icon: Link2, title: "6. Mutual Witness", text: "If your witness also has a declaration, you may witness theirs. Then both records become Mutually Witnessed." },
  { icon: Lock, title: "7. Lock", text: "WitnessYourVote locks the witnessed version so it cannot silently change." },
  { icon: Fingerprint, title: "8. Receipt", text: "You receive your verification receipt and QR code — keep it on your phone." },
  { icon: Search, title: "9. Verify", text: "The record can be checked on the public verification page anytime, without exposing your private information." },
];

export default function HowItWorks() {
  const copy = usePlatformCopy();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-3xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="how-it-works-title">
          How Witness Your Vote works
        </h1>
        <p className="text-muted-foreground mt-4 text-base leading-relaxed">
          Witness Your Vote creates an independent, securely preserved record of eligible electors who voluntarily
          declare which municipal candidate they voted for. It never replaces the official election — it makes the
          count independently checkable.
        </p>
      </div>

      <div className="mt-8 max-w-3xl">
        <BallotWarning testId="how-ballot-secrecy-warning" />
      </div>

      <div className="mt-10 space-y-4 max-w-3xl">
        {DETAIL_STEPS.map((step) => (
          <Card key={step.title}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="h-11 w-11 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                  <step.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-heading font-semibold text-lg text-foreground mb-2">{step.title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.text}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* MUTUAL WITNESS — swim-lane process */}
      <section id="mutual-witness" className="mt-14 max-w-4xl scroll-mt-24" aria-labelledby="mutual-witness-title">
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Mutual Witness</Badge>
        <h2 id="mutual-witness-title" className="font-heading font-bold text-2xl sm:text-3xl text-foreground" data-testid="how-mutual-witness-title">
          Don't just declare it. Witness it.
        </h2>
        <p className="text-muted-foreground mt-3 leading-relaxed max-w-3xl">
          A witness makes your declaration stronger. Your witness confirms{" "}
          <span className="font-semibold text-foreground">you made and signed your declaration</span> — they{" "}
          <span className="font-semibold text-foreground">never see your ballot</span> and never learn how your
          official vote was counted. If you witness each other, both records become{" "}
          <span className="font-semibold text-foreground">Mutually Witnessed</span>.
        </p>

        {/* Swim-lane diagram: FOUR lanes — mobile-first (stacks on small screens, no zooming needed) */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="mutual-witness-swimlane">
          {[
            {
              lane: "Voter",
              icon: FileCheck2,
              cls: "border-blue-200",
              chip: "bg-blue-100 text-blue-800",
              steps: ["Create Declaration", "Verify Email", "Sign Declaration",
                      "Choose Witness OR Request Witness", "Receive Witness Confirmation", "Receive Receipt"],
            },
            {
              lane: "Witness",
              icon: HandHeart,
              cls: "border-emerald-200",
              chip: "bg-emerald-100 text-emerald-800",
              steps: ["Receive Witness Request", "Accept", "Open Declaration", "Confirm Participant",
                      "Sign Witness Attestation", "Optionally Send Own Declaration Back"],
            },
            {
              lane: "WitnessYourVote",
              icon: ShieldCheck,
              cls: "border-slate-300",
              chip: "bg-slate-200 text-slate-800",
              steps: ["Verify Email", "Create Secure Witness Token", "Match Witness if requested",
                      "Timestamp Signatures", "Lock Declaration Version", "Generate Integrity Record",
                      "Create Receipt + QR", "Update Aggregate Stats"],
            },
            {
              lane: "Admin / Security",
              icon: ScrollText,
              cls: "border-amber-200",
              chip: "bg-amber-100 text-amber-800",
              steps: ["Review Fraud Signals", "Review Failed Emails", "Review Witness Network Problems",
                      "Review Privacy / Security Alerts"],
            },
          ].map((lane) => (
            <Card key={lane.lane} className={lane.cls}>
              <CardContent className="p-4">
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${lane.chip}`}>
                  <lane.icon className="h-3 w-3" /> {lane.lane}
                </span>
                <ol className="mt-3 space-y-0">
                  {lane.steps.map((s, i) => (
                    <li key={i}>
                      <div className="flex items-start gap-2.5">
                        <span className="h-5 w-5 shrink-0 rounded-full bg-secondary text-primary text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                        <p className="text-[13px] text-foreground leading-snug py-0.5">{s}</p>
                      </div>
                      {i < lane.steps.length - 1 && <div className="ml-2.5 h-3 border-l-2 border-slate-200" aria-hidden="true" />}
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-4 rounded-xl border border-blue-300 bg-blue-50 p-5 flex items-start gap-3" data-testid="mutual-witness-result">
          <Link2 className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
          <p className="text-sm text-blue-900 leading-relaxed">
            <span className="font-bold">Result: both declarations become Mutually Witnessed.</span>{" "}
            Each record locks, gets a timestamp and an integrity fingerprint, and can be verified anytime with its
            receipt or QR code. Both people also confirm their email address before Mutual Witness status is granted.
          </p>
        </div>

        <Card className="mt-4 bg-secondary border-slate-200">
          <CardContent className="p-5 flex items-start gap-3">
            <EyeOff className="h-5 w-5 text-accent shrink-0 mt-0.5" />
            <p className="text-sm text-foreground leading-relaxed" data-testid="mutual-witness-ballot-note">
              <span className="font-semibold">Your witness never sees your ballot.</span> They are confirming that you
              personally made and signed your voluntary WitnessYourVote declaration — not how your official ballot was
              marked or counted. Never show anyone your marked ballot, and never photograph or upload it.
            </p>
          </CardContent>
        </Card>
        <p className="text-xs text-muted-foreground mt-3">
          If you change your declaration after it was witnessed, the old version stays on file, the witness signature
          is cancelled for the new version, and your declaration must be witnessed again.
        </p>
        <div className="flex flex-wrap gap-3 mt-5">
          <Button asChild className="bg-accent hover:bg-accent/90" data-testid="how-mutual-create-cta">
            <Link to="/declare">Create My Declaration</Link>
          </Button>
          <Button asChild variant="outline" data-testid="how-need-witness-cta">
            <Link to="/witness-network">I Need a Witness</Link>
          </Button>
          <Button asChild variant="outline" data-testid="how-mutual-witness-code-cta">
            <Link to="/witness">I Have a Witness Code</Link>
          </Button>
        </div>
      </section>

      <div className="mt-12 max-w-3xl">
        <h2 className="font-heading font-bold text-2xl text-foreground mb-5">What keeps it trustworthy</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { icon: ShieldCheck, title: "Independent by design", text: "Candidates get public campaign tools but never control the private declaration database or the verification process." },
            { icon: EyeOff, title: "No live totals — for anyone", text: "While voting is open, no declaration counts are visible to the public, to candidates, or in any embed. No leaderboards, ever." },
            { icon: Lock, title: "Private, preserved evidence", text: "Declarations are never shown publicly. Records are never silently deleted; every administrative action is logged in an append-only audit trail." },
            { icon: Fingerprint, title: "Verifiable receipts", text: "Your SHA-256 receipt lets you — or any independent reviewer — confirm your record still exists, unaltered." },
          ].map((f) => (
            <Card key={f.title}>
              <CardContent className="p-5">
                <f.icon className="h-5 w-5 text-accent mb-3" />
                <h3 className="font-heading font-semibold text-sm mb-1.5">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="mt-12 max-w-3xl">
        <Card className="bg-secondary">
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground leading-relaxed mb-4">{copy.disclaimers?.principle}</p>
            <div className="flex flex-wrap gap-3">
              <Button asChild data-testid="how-cta-find-candidate">
                <Link to="/find-a-candidate">Find My Candidate</Link>
              </Button>
              <Button asChild variant="outline" data-testid="how-cta-verify">
                <Link to="/verify">Verify a Declaration</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
